import React from 'react';
import { Users, Building2, Calendar, TrendingUp, ArrowRight, Plus } from 'lucide-react';
import { Tenant, Event, FeedPost } from '../../types';
import { events, feedPosts } from '../../data/mockData';
interface ClubDashboardProps {
  tenant: Tenant;
  onNavigate: (page: string, id?: string) => void;
}
export function ClubDashboard({
  tenant,
  onNavigate
}: ClubDashboardProps) {
  const upcomingEvents = events.filter(e => e.tenantId === tenant.id && e.status === 'upcoming').slice(0, 3);
  const recentPosts = feedPosts.filter(p => p.tenantId === tenant.id).slice(0, 3);
  return <div className="space-y-8" data-id="element-221">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-222">
        <div data-id="element-223">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-224">
            Willkommen im {tenant.name} Netzwerk
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-225">
            Hier ist der Überblick über die Aktivitäten Ihres Business Clubs.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-226">
          <button onClick={() => onNavigate('events')} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-227">
            <Plus size={16} data-id="element-228" /> Event erstellen
          </button>
          <button onClick={() => onNavigate('network-people')} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm" data-id="element-229">
            <Plus size={16} data-id="element-230" /> Mitglied einladen
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-231">
        {[{
        label: 'Mitglieder',
        value: tenant.memberCount,
        change: '+12 diesen Monat',
        icon: Users,
        color: 'text-blue-600',
        bg: 'bg-blue-50'
      }, {
        label: 'Unternehmen',
        value: tenant.companyCount,
        change: '+3 diesen Monat',
        icon: Building2,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50'
      }, {
        label: 'Kommende Events',
        value: upcomingEvents.length,
        change: 'Nächstes in 4 Tagen',
        icon: Calendar,
        color: 'text-purple-600',
        bg: 'bg-purple-50'
      }, {
        label: 'Netzwerk-Aktivität',
        value: 'Hoch',
        change: '24% über Vormonat',
        icon: TrendingUp,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50'
      }].map((stat, i) => <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-232">
            <div className="flex items-center justify-between mb-4" data-id="element-233">
              <div className={`p-2.5 rounded-lg ${stat.bg} ${stat.color}`} data-id="element-234">
                <stat.icon size={20} data-id="element-235" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-236">{stat.value}</p>
            <div className="flex items-center justify-between mt-1" data-id="element-237">
              <p className="text-sm font-medium text-slate-500" data-id="element-238">{stat.label}</p>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-239">
                {stat.change}
              </span>
            </div>
          </div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-240">
        {/* Upcoming Events */}
        <div className="space-y-4" data-id="element-241">
          <div className="flex items-center justify-between" data-id="element-242">
            <h2 className="text-lg font-bold text-slate-800" data-id="element-243">Nächste Events</h2>
            <button onClick={() => onNavigate('events')} className="text-sm font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-244">
              Alle Events <ArrowRight size={16} data-id="element-245" />
            </button>
          </div>

          <div className="space-y-3" data-id="element-246">
            {upcomingEvents.map(event => <div key={event.id} onClick={() => onNavigate('event-detail', event.id)} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex items-center gap-4 group" data-id="element-247">
                <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-100 flex flex-col items-center justify-center flex-shrink-0" data-id="element-248">
                  <span className="text-xs font-bold text-red-500 uppercase" data-id="element-249">
                    {new Date(event.date).toLocaleDateString('de-DE', {
                  month: 'short'
                })}
                  </span>
                  <span className="text-lg font-bold text-slate-800" data-id="element-250">
                    {new Date(event.date).getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-251">
                  <h3 className="font-bold text-slate-900 truncate group-hover:text-emerald-600 transition-colors" data-id="element-252">
                    {event.title}
                  </h3>
                  <p className="text-sm text-slate-500 flex items-center gap-2 mt-1" data-id="element-253">
                    <span data-id="element-254">{event.time} Uhr</span>
                    <span data-id="element-255">•</span>
                    <span className="truncate" data-id="element-256">{event.location}</span>
                  </p>
                </div>
                <div className="hidden sm:block text-right" data-id="element-257">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" data-id="element-258">
                    {event.attendeeCount} / {event.maxAttendees}
                  </span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="space-y-4" data-id="element-259">
          <div className="flex items-center justify-between" data-id="element-260">
            <h2 className="text-lg font-bold text-slate-800" data-id="element-261">
              Neues aus dem Netzwerk
            </h2>
            <button onClick={() => onNavigate('feed')} className="text-sm font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-262">
              Zum Feed <ArrowRight size={16} data-id="element-263" />
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-264">
            <div className="divide-y divide-slate-100" data-id="element-265">
              {recentPosts.map(post => <div key={post.id} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-266">
                  <div className="flex items-start gap-3" data-id="element-267">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600 flex-shrink-0" data-id="element-268">
                      {post.authorAvatar}
                    </div>
                    <div data-id="element-269">
                      <div className="flex items-baseline gap-2" data-id="element-270">
                        <span className="font-bold text-slate-900 text-sm" data-id="element-271">
                          {post.authorName}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-272">
                          {post.createdAt}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2" data-id="element-273">
                        {post.authorRole} bei {post.authorCompany}
                      </p>
                      <p className="text-sm text-slate-700 line-clamp-2" data-id="element-274">
                        {post.content}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}